import { createContext, FC, ReactNode, useContext, useEffect, useState } from 'react';
import { noticeBaseUrl } from '../../../../baseUrl';
import { errorToast } from '../../../../CommonComponent/toast';
import useFetchWithAuth from '../../../../CommonHooks/useFetchWithAuth';
import { useMainContainerProvider } from '../../../../mainContainerProvider';
import { CircularType } from '../types';

interface CircularContextType {
  isDeliveredLoading: boolean;
  isTemplateLoading: boolean;
  setIsTemplateLoading: React.Dispatch<React.SetStateAction<boolean>>;
  handleGetTemplateCircularList: () => Promise<void>;
  handleGetDeliveredCircularList: () => Promise<void>;
  setIsDeliveredLoading: React.Dispatch<React.SetStateAction<boolean>>;
  createDialogOpen: boolean;
  setCreateDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  editDialogOpen: boolean;
  setEditDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  circularDeliveredList: CircularType[];
  setCircularDeliveredList: React.Dispatch<React.SetStateAction<CircularType[]>>;
  circularTemplateList: CircularType[];
  setCircularTemplateList: React.Dispatch<React.SetStateAction<CircularType[]>>;
  setSelectedType: React.Dispatch<React.SetStateAction<string>>;
  selectedType: string;
}

const initialCircularList: CircularContextType = {
  isDeliveredLoading: false,
  isTemplateLoading: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setIsTemplateLoading: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  handleGetTemplateCircularList: async () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  handleGetDeliveredCircularList: async () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setIsDeliveredLoading: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setCreateDialogOpen: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setEditDialogOpen: () => {},
  createDialogOpen: false,
  editDialogOpen: false,
  circularDeliveredList: [],
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setCircularDeliveredList: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setSelectedType: () => {},
  selectedType: '',
  circularTemplateList: [],
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setCircularTemplateList: () => {},
};

const CircularContext = createContext<CircularContextType>(initialCircularList);
const CircularProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [isDeliveredLoading, setIsDeliveredLoading] = useState<boolean>(false);
  const [isTemplateLoading, setIsTemplateLoading] = useState<boolean>(false);
  const [circularDeliveredList, setCircularDeliveredList] = useState<CircularType[]>([]);
  const [circularTemplateList, setCircularTemplateList] = useState<CircularType[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState<boolean>(false);
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const { uid, instituteId } = useMainContainerProvider();
  const { fetchWithAuth } = useFetchWithAuth();
  const [selectedType, setSelectedType] = useState<string>('delivered');

  const handleGetDeliveredCircularList = async () => {
    setIsDeliveredLoading(true);
    const { error, data } = await fetchWithAuth(`notice-list/?uid=${uid}&noticeStatus=delivered&instituteId=${instituteId}&type=circular`, {}, noticeBaseUrl);
    setIsDeliveredLoading(false);
    if (error) {
      errorToast(error);
    } else {
      setCircularDeliveredList(data);
    }
  };

  const handleGetTemplateCircularList = async () => {
    setIsTemplateLoading(true);
    const { error, data } = await fetchWithAuth(`notice-list/?uid=${uid}&noticeStatus=template&instituteId=${instituteId}&type=circular`, {}, noticeBaseUrl);
    setIsTemplateLoading(false);
    if (error) {
      errorToast(error);
    } else {
      setCircularTemplateList(data);
    }
  };

  useEffect(() => {
    handleGetDeliveredCircularList();
    handleGetTemplateCircularList();
    // eslint-disable-next-line
  }, []);
  return (
    <CircularContext.Provider
      value={{
        isDeliveredLoading,
        setIsDeliveredLoading,
        isTemplateLoading,
        setIsTemplateLoading,
        circularDeliveredList,
        setCircularDeliveredList,
        circularTemplateList,
        setCircularTemplateList,
        setCreateDialogOpen,
        setEditDialogOpen,
        createDialogOpen,
        editDialogOpen,
        handleGetTemplateCircularList,
        handleGetDeliveredCircularList,
        setSelectedType,
        selectedType,
      }}
    >
      {children}
    </CircularContext.Provider>
  );
};

export default CircularProvider;

export const useCircularProvider = () => useContext(CircularContext);
